/**
 * Client Detail Page - Sprint 3
 * Sprint 11A: Updated with Layout component and Avida branding
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clientsAPI, documentsAPI, documentTypesAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  RefreshCw, ChevronLeft, Save, CheckCircle2, XCircle, 
  AlertTriangle, FileText, Upload, ArrowRight, Archive, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Prospect: 'bg-slate-100 text-slate-700 border-slate-300',
  Admitted: 'bg-blue-100 text-blue-700 border-blue-300',
  Active: 'bg-green-100 text-green-700 border-green-300',
  Discharged: 'bg-amber-100 text-amber-700 border-amber-300',
  Archived: 'bg-red-100 text-red-700 border-red-300'
};

const PAYER_PROGRAMS = [
  { value: 'Family Care', label: 'Family Care' },
  { value: 'IRIS', label: 'IRIS' },
  { value: 'iCare', label: 'iCare' },
  { value: 'Straight Medicaid', label: 'Straight Medicaid' },
  { value: 'Private Pay', label: 'Private Pay' }
];

const ACTIVATION_REQUIRED_DOCS = [
  'CLIENT_RIGHTS_ACK',
  'CLIENT_COMPLAINT_INFO_ACK', 
  'CLIENT_ROI_AUTHORIZATION',
  'CLIENT_PCP_ORDER',
  'CLIENT_PLAN_OF_CARE',
  'CLIENT_SERVICE_AUTHORIZATION'
];

const ClientDetailPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { user, logout, loading: authLoading, canUpdateClients, canArchiveClients, canUploadDocuments } = useAuth();
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Compliance tab state
  const [activationGate, setActivationGate] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  
  // Documents tab state
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  // Status change dialogs
  const [showAdmitDialog, setShowAdmitDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDischargeDialog, setShowDischargeDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [dischargeDate, setDischargeDate] = useState('');
  const [dischargeReason, setDischargeReason] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  
  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchClient = useCallback(async () => {
    setLoading(true);
    try {
      const response = await clientsAPI.get(clientId);
      setClient(response.data);
      setEditData(response.data);
    } catch (err) {
      toast.error('Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate]);

  const fetchCompliance = useCallback(async () => {
    setLoadingCompliance(true);
    try {
      const [gateRes, completenessRes] = await Promise.all([
        clientsAPI.checkActivationGate(clientId),
        documentsAPI.completeness('Client', clientId)
      ]);
      setActivationGate(gateRes.data);
      setCompleteness(completenessRes.data);
    } catch (err) {
      console.error('Failed to load compliance data', err);
    } finally {
      setLoadingCompliance(false);
    }
  }, [clientId]);

  const fetchDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const [docsRes, typesRes] = await Promise.all([
        documentsAPI.list({ linked_entity_type: 'Client', linked_entity_id: clientId }),
        documentTypesAPI.list({ category: 'Client', is_active: true })
      ]);
      setDocuments(docsRes.data.documents);
      setDocumentTypes(typesRes.data.document_types);
    } catch (err) {
      console.error('Failed to load documents', err);
    } finally {
      setLoadingDocs(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      const fields = ['first_name', 'last_name', 'dob', 'phone', 'email', 'address_line1', 
                     'address_line2', 'city', 'state', 'zip', 'preferred_language', 
                     'payer_program', 'payer_mco', 'medicaid_id'];
      
      for (const field of fields) {
        if (editData[field] !== client[field]) {
          updates[field] = editData[field] || null;
        }
      }
      
      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setEditMode(false);
        return;
      }
      
      await clientsAPI.update(clientId, updates);
      toast.success('Client updated successfully');
      setEditMode(false);
      fetchClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus, reason = null, extraData = {}) => {
    setStatusChangeLoading(true);
    try {
      const data = { new_status: newStatus, reason, ...extraData };
      await clientsAPI.changeStatus(clientId, data);
      toast.success(`Client status changed to ${newStatus}`);
      fetchClient();
      fetchCompliance();
      
      // Close dialogs
      setShowAdmitDialog(false);
      setShowActivateDialog(false);
      setShowDischargeDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change status');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleArchive = async () => {
    if (archiveReason.length < 10) {
      toast.error('Archive reason must be at least 10 characters');
      return;
    }
    
    setStatusChangeLoading(true);
    try {
      await clientsAPI.archive(clientId, archiveReason);
      toast.success('Client archived');
      setShowArchiveDialog(false);
      setArchiveReason('');
      fetchClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive client');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadDocType) {
      toast.error('Please select a file and document type');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type_code', uploadDocType);
      formData.append('linked_entity_type', 'Client');
      formData.append('linked_entity_id', clientId);
      
      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadDocType('');
      fetchDocuments();
      fetchCompliance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const isArchived = client.status === 'Archived';

  // Determine next available action
  const getNextAction = () => {
    switch (client.status) {
      case 'Prospect': return { label: 'Admit Client', action: () => setShowAdmitDialog(true), color: 'bg-avida-blue hover:bg-avida-blue/90' };
      case 'Admitted': return { label: 'Activate Client', action: () => { fetchCompliance(); setShowActivateDialog(true); }, color: 'bg-green-600 hover:bg-green-700' };
      case 'Active': return { label: 'Discharge Client', action: () => setShowDischargeDialog(true), color: 'bg-amber-600 hover:bg-amber-700' };
      default: return null;
    }
  };
  const nextAction = getNextAction();

  return (
    <Layout title={`${client.last_name}, ${client.first_name}`}>
      <div data-testid="client-detail-page">
        {/* Back button and status badges */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/clients')}>
              <ChevronLeft className="h-4 w-4 mr-1" />Clients
            </Button>
            <div className="flex items-center gap-2">
              <Badge className={STATUS_COLORS[client.status]}>{client.status}</Badge>
              {client.medicaid_id && <span className="text-sm text-slate-500">ID: {client.medicaid_id}</span>}
            </div>
          </div>
        </div>

        {/* Status Actions Bar */}
        {!isArchived && (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Current Status:</span>
                  <Badge className={`${STATUS_COLORS[client.status]} text-sm px-3 py-1`}>{client.status}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  {nextAction && canUpdateClients && (
                    <Button 
                      className={nextAction.color}
                      onClick={nextAction.action}
                      data-testid="next-status-action"
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />{nextAction.label}
                    </Button>
                  )}
                  {canArchiveClients && client.status !== 'Active' && (
                    <Button 
                      variant="outline" 
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setShowArchiveDialog(true)}
                      data-testid="archive-client-btn"
                    >
                      <Archive className="h-4 w-4 mr-2" />Archive
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance" onClick={fetchCompliance}>Compliance</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" onClick={fetchDocuments}>Documents</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Client Information</CardTitle>
                    <CardDescription>Demographics and contact details</CardDescription>
                  </div>
                  {canUpdateClients && !isArchived && (
                    <div>
                      {editMode ? (
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => { setEditMode(false); setEditData(client); }}>Cancel</Button>
                          <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">
                            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-client-btn">Edit</Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">Personal Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>First Name</Label>
                        {editMode ? (
                          <Input value={editData.first_name || ''} onChange={(e) => setEditData({...editData, first_name: e.target.value})} />
                        ) : (
                          <p className="text-slate-900 mt-1">{client.first_name}</p>
                        )}
                      </div>
                      <div>
                        <Label>Last Name</Label>
                        {editMode ? (
                          <Input value={editData.last_name || ''} onChange={(e) => setEditData({...editData, last_name: e.target.value})} />
                        ) : (
                          <p className="text-slate-900 mt-1">{client.last_name}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Date of Birth</Label>
                      {editMode ? (
                        <Input type="date" value={editData.dob || ''} onChange={(e) => setEditData({...editData, dob: e.target.value})} />
                      ) : (
                        <p className="text-slate-900 mt-1">{client.dob || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label>Preferred Language</Label>
                      {editMode ? (
                        <Input value={editData.preferred_language || ''} onChange={(e) => setEditData({...editData, preferred_language: e.target.value})} />
                      ) : (
                        <p className="text-slate-900 mt-1">{client.preferred_language || '-'}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">Contact Information</h4>
                    <div>
                      <Label>Phone</Label>
                      {editMode ? (
                        <Input value={editData.phone || ''} onChange={(e) => setEditData({...editData, phone: e.target.value})} />
                      ) : (
                        <p className="text-slate-900 mt-1">{client.phone || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label>Email</Label>
                      {editMode ? (
                        <Input type="email" value={editData.email || ''} onChange={(e) => setEditData({...editData, email: e.target.value})} />
                      ) : (
                        <p className="text-slate-900 mt-1">{client.email || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label>Address</Label>
                      {editMode ? (
                        <div className="space-y-2">
                          <Input placeholder="Address Line 1" value={editData.address_line1 || ''} onChange={(e) => setEditData({...editData, address_line1: e.target.value})} />
                          <Input placeholder="Address Line 2" value={editData.address_line2 || ''} onChange={(e) => setEditData({...editData, address_line2: e.target.value})} />
                          <div className="grid grid-cols-3 gap-2">
                            <Input placeholder="City" value={editData.city || ''} onChange={(e) => setEditData({...editData, city: e.target.value})} />
                            <Input placeholder="State" value={editData.state || 'WI'} onChange={(e) => setEditData({...editData, state: e.target.value})} />
                            <Input placeholder="ZIP" value={editData.zip || ''} onChange={(e) => setEditData({...editData, zip: e.target.value})} />
                          </div>
                        </div>
                      ) : (
                        <div className="text-slate-900 mt-1">
                          {client.address_line1 || client.city ? (
                            <>
                              {client.address_line1 && <p>{client.address_line1}</p>}
                              {client.address_line2 && <p>{client.address_line2}</p>}
                              {(client.city || client.state || client.zip) && (
                                <p>{[client.city, client.state, client.zip].filter(Boolean).join(', ')}</p>
                              )}
                            </>
                          ) : '-'}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">Insurance/Payer</h4>
                    <div>
                      <Label>Payer Program</Label>
                      {editMode ? (
                        <Select value={editData.payer_program || ''} onValueChange={(v) => setEditData({...editData, payer_program: v})}>
                          <SelectTrigger><SelectValue placeholder="Select program" /></SelectTrigger>
                          <SelectContent>
                            {PAYER_PROGRAMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-slate-900 mt-1">{client.payer_program || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label>MCO</Label>
                      {editMode ? (
                        <Input value={editData.payer_mco || ''} onChange={(e) => setEditData({...editData, payer_mco: e.target.value})} />
                      ) : (
                        <p className="text-slate-900 mt-1">{client.payer_mco || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label>Medicaid ID</Label>
                      {editMode ? (
                        <Input value={editData.medicaid_id || ''} onChange={(e) => setEditData({...editData, medicaid_id: e.target.value})} />
                      ) : (
                        <p className="text-slate-900 mt-1 font-mono">{client.medicaid_id || '-'}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">Dates</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Intake Date</Label>
                        <p className="text-slate-900 mt-1">{client.intake_date ? new Date(client.intake_date).toLocaleDateString() : '-'}</p>
                      </div>
                      <div>
                        <Label>Admission Date</Label>
                        <p className="text-slate-900 mt-1">{client.admission_date ? new Date(client.admission_date).toLocaleDateString() : '-'}</p>
                      </div>
                      <div>
                        <Label>Activation Date</Label>
                        <p className="text-slate-900 mt-1">{client.activation_date ? new Date(client.activation_date).toLocaleDateString() : '-'}</p>
                      </div>
                      <div>
                        <Label>Discharge Date</Label>
                        <p className="text-slate-900 mt-1">{client.discharge_date ? new Date(client.discharge_date).toLocaleDateString() : '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <div className="space-y-6">
              {/* Activation Gate Status */}
              <Card data-testid="activation-gate-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {activationGate?.can_activate ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    Activation Gate
                  </CardTitle>
                  <CardDescription>Required documents for client activation</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingCompliance ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  ) : activationGate ? (
                    <div className="space-y-4">
                      <div className={`p-4 rounded-lg ${activationGate.can_activate ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <p className={activationGate.can_activate ? 'text-green-800' : 'text-red-800'}>
                          {activationGate.message}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <h4 className="font-medium text-slate-900">Required Documents Checklist:</h4>
                        {ACTIVATION_REQUIRED_DOCS.map(docCode => {
                          const isMissing = activationGate.missing_documents.includes(docCode);
                          const isExpired = activationGate.expired_documents.includes(docCode);
                          const isValid = !isMissing && !isExpired;
                          
                          return (
                            <div key={docCode} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                              {isValid && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                              {isMissing && <XCircle className="h-4 w-4 text-red-600" />}
                              {isExpired && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                              <span className={`text-sm ${isValid ? 'text-green-800' : isMissing ? 'text-red-800' : 'text-amber-800'}`}>
                                {docCode.replace(/_/g, ' ')}
                              </span>
                              {isMissing && <Badge variant="outline" className="bg-red-100 text-red-700 ml-auto">Missing</Badge>}
                              {isExpired && <Badge variant="outline" className="bg-amber-100 text-amber-700 ml-auto">Expired</Badge>}
                              {isValid && <Badge variant="outline" className="bg-green-100 text-green-700 ml-auto">Valid</Badge>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500">Click to load compliance data</p>
                  )}
                </CardContent>
              </Card>

              {/* Overall Completeness */}
              {completeness && (
                <Card data-testid="completeness-card">
                  <CardHeader>
                    <CardTitle>Document Completeness</CardTitle>
                    <CardDescription>Overall document status for this client</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold">{completeness.percent_complete}%</span>
                        <span className="text-slate-500">{completeness.required_types_present} of {completeness.total_required_types} required docs</span>
                      </div>
                      <Progress value={completeness.percent_complete} className="h-3" />
                      
                      {completeness.has_expired_documents && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                          <span className="text-amber-800">Expired: {completeness.expired_document_codes.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Client Documents</CardTitle>
                    <CardDescription>{documents.length} documents uploaded</CardDescription>
                  </div>
                  {canUploadDocuments && !isArchived && (
                    <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-doc-btn">
                      <Upload className="h-4 w-4 mr-2" />Upload Document
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loadingDocs ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No documents uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-slate-400" />
                          <div>
                            <p className="font-medium text-slate-900">{doc.document_type_name || doc.document_type_code}</p>
                            <p className="text-sm text-slate-500">{doc.file_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">v{doc.version}</Badge>
                          {doc.status === 'Active' && !doc.is_expired && (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          )}
                          {doc.status === 'Active' && doc.is_expired && (
                            <Badge className="bg-red-100 text-red-700">Expired</Badge>
                          )}
                          {doc.status === 'Archived' && (
                            <Badge className="bg-slate-100 text-slate-600">Archived</Badge>
                          )}
                          <span className="text-sm text-slate-500">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

      {/* Admit Dialog */}
      <Dialog open={showAdmitDialog} onOpenChange={setShowAdmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admit Client</DialogTitle>
            <DialogDescription>Change client status from Prospect to Admitted</DialogDescription>
          </DialogHeader>
          <p className="text-slate-600">This will mark the client as Admitted and record the admission date.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdmitDialog(false)}>Cancel</Button>
            <Button onClick={() => handleStatusChange('Admitted')} disabled={statusChangeLoading} data-testid="confirm-admit">
              {statusChangeLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Admission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Activate Client</DialogTitle>
            <DialogDescription>Change client status from Admitted to Active</DialogDescription>
          </DialogHeader>
          {activationGate && !activationGate.can_activate ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">Cannot activate client</p>
                <p className="text-red-700 text-sm mt-1">{activationGate.message}</p>
              </div>
              <p className="text-sm text-slate-500">Please upload the required documents before activating.</p>
            </div>
          ) : (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">All required documents are present. Client can be activated.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => handleStatusChange('Active')} 
              disabled={statusChangeLoading || (activationGate && !activationGate.can_activate)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-activate"
            >
              {statusChangeLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Activate Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discharge Dialog */}
      <Dialog open={showDischargeDialog} onOpenChange={setShowDischargeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discharge Client</DialogTitle>
            <DialogDescription>Change client status from Active to Discharged</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Discharge Date *</Label>
              <Input 
                type="date" 
                value={dischargeDate} 
                onChange={(e) => setDischargeDate(e.target.value)}
                data-testid="discharge-date-input"
              />
            </div>
            <div>
              <Label>Discharge Reason * (min 10 characters)</Label>
              <Textarea 
                value={dischargeReason}
                onChange={(e) => setDischargeReason(e.target.value)}
                placeholder="Enter reason for discharge..."
                data-testid="discharge-reason-input"
              />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <Clock className="h-4 w-4 inline mr-1" />
              Discharge summary will be due 30 days after discharge date.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDischargeDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => handleStatusChange('Discharged', dischargeReason, { discharge_date: dischargeDate })} 
              disabled={statusChangeLoading || !dischargeDate || dischargeReason.length < 10}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-discharge"
            >
              {statusChangeLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Discharge Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Client</DialogTitle>
            <DialogDescription>This action cannot be undone. The client will be moved to archived status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Archive Reason * (min 10 characters)</Label>
              <Textarea 
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder="Enter reason for archiving..."
                data-testid="archive-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleArchive} 
              disabled={statusChangeLoading || archiveReason.length < 10}
              data-testid="confirm-archive"
            >
              {statusChangeLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Archive Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload a document for this client</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Document Type *</Label>
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger data-testid="upload-doc-type">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(dt => (
                    <SelectItem key={dt.code} value={dt.code}>{dt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File *</Label>
              <Input 
                type="file" 
                onChange={(e) => setUploadFile(e.target.files[0])}
                data-testid="upload-file-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadDocType} data-testid="confirm-upload">
              {uploading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
};

export default ClientDetailPage;
